/**
 * <UiNode> — polymorphic helper that stamps `data-ui-*` attributes per the
 * UI Instrumentation Pack. The `name` prop is typed against the registry
 * so unknown names fail at compile time.
 *
 * No ref forwarding for now; callers that need a ref pass it through `ref`
 * which the underlying element receives directly via prop-spread. React 19
 * treats `ref` as a normal prop on DOM elements.
 */

import type { ElementType, ComponentPropsWithRef } from 'react';
import type { UiNodeName } from './ui-registry';

type UiNodeProps<E extends ElementType> = {
  as?: E;
  name: UiNodeName;
  entity?: string;
  entityId?: string;
  action?: string;
  state?: string;
  label?: string;
} & Omit<ComponentPropsWithRef<E>, 'as' | 'name'>;

export function UiNode<E extends ElementType = 'div'>(props: UiNodeProps<E>) {
  const { as, name, entity, entityId, action, state, label, ...rest } = props as UiNodeProps<E> & {
    as?: ElementType;
  };
  const Tag = (as ?? 'div') as ElementType;
  const dataAttrs: Record<string, string> = { 'data-ui-name': name };
  if (entity) dataAttrs['data-ui-entity'] = entity;
  if (entityId) dataAttrs['data-ui-entity-id'] = entityId;
  if (action) dataAttrs['data-ui-action'] = action;
  if (state) dataAttrs['data-ui-state'] = state;
  if (label) dataAttrs['data-ui-label'] = label;
  return <Tag {...dataAttrs} {...(rest as Record<string, unknown>)} />;
}
